import { useState, useEffect, useCallback } from 'react'
import {
  Message,
  ConversationSession,
  createNewSession,
  loadConversations,
  saveSession,
  addMessageToSession,
  getCurrentSession,
  setCurrentSession,
  deleteSession,
  clearAllSessions
} from '../utils/conversationStorage.ts'

export const useConversationHistory = (userId?: string) => {
  const [currentSession, setCurrentSessionState] = useState<ConversationSession | null>(null)
  const [allSessions, setAllSessions] = useState<ConversationSession[]>([])

  useEffect(() => {
    const conversations = loadConversations(userId)
    const session = getCurrentSession(userId)
    
    setCurrentSessionState(session)
    setAllSessions(conversations.sessions)
  }, [userId])

  const createSession = useCallback((): ConversationSession => {
    const newSession = createNewSession()
    saveSession(newSession, userId)
    setCurrentSessionState(newSession)
    const conversations = loadConversations(userId)
    setAllSessions(conversations.sessions)
    return newSession
  }, [userId])

  const loadSession = useCallback((sessionId: string): ConversationSession | null => {
    const conversations = loadConversations(userId)
    const session = conversations.sessions.find(s => s.sessionId === sessionId)
    
    if (session) {
      setCurrentSession(sessionId, userId)
      const updatedConversations = loadConversations(userId)
      const updatedSession = updatedConversations.sessions.find(s => s.sessionId === sessionId)
      
      if (updatedSession) {
        updatedSession.isActive = true
        setCurrentSessionState(updatedSession)
        setAllSessions(updatedConversations.sessions)
        return updatedSession
      }
    }
    
    return null
  }, [userId])

  const addMessage = useCallback((message: Message, targetSessionId?: string): void => {
    let sessionId = targetSessionId
    
    if (!sessionId) {
      if (currentSession) {
        sessionId = currentSession.sessionId
      } else {
        const conversations = loadConversations(userId)
        if (conversations.currentSessionId) {
          sessionId = conversations.currentSessionId
        }
      }
    }
    
    if (!sessionId) {
      return
    }

    const updated = addMessageToSession(sessionId, message, userId)
    if (updated) {
      setCurrentSessionState(updated)
      const conversations = loadConversations(userId)
      setAllSessions(conversations.sessions)
    }
  }, [currentSession, userId])

  const updateCurrentSession = useCallback((updates: Partial<ConversationSession>): void => {
    if (!currentSession) {
      return
    }

    const updated: ConversationSession = {
      ...currentSession,
      ...updates,
      lastUpdated: new Date().toISOString()
    }

    saveSession(updated, userId)
    setCurrentSessionState(updated)
    const conversations = loadConversations(userId)
    setAllSessions(conversations.sessions)
  }, [currentSession, userId])

  const removeSession = useCallback((sessionId: string): void => {
    deleteSession(sessionId, userId)
    const conversations = loadConversations(userId)
    setAllSessions(conversations.sessions)
    
    if (currentSession?.sessionId === sessionId) {
      setCurrentSessionState(null)
      setCurrentSession(null, userId)
    }
  }, [currentSession, userId])

  const clearAll = useCallback((): void => {
    clearAllSessions(userId)
    setCurrentSessionState(null)
    setAllSessions([])
  }, [userId])

  const getSessions = useCallback((): ConversationSession[] => {
    return loadConversations(userId).sessions
  }, [userId])

  const clearCurrentSession = useCallback((): void => {
    if (currentSession) {
      const updated: ConversationSession = {
        ...currentSession,
        isActive: false,
        lastUpdated: new Date().toISOString()
      }
      saveSession(updated, userId)
    }
    setCurrentSessionState(null)
    setCurrentSession(null, userId)
  }, [currentSession, userId])

  return {
    currentSession,
    allSessions,
    createSession,
    loadSession,
    addMessage,
    updateCurrentSession,
    removeSession,
    clearAll,
    getSessions,
    clearCurrentSession
  }
}

